import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  BarChart3,
  Activity,
  Fingerprint,
  UserCheck,
  Mail,
  Tag,
  TrendingUp,
  IndianRupee,
  Link as LinkIcon,
  FileText,
  Clock,
  CalendarClock,
  Brain,
  Code,
  AlertCircle,
  Settings,
  LogOut,
  ChevronDown,
  Lightbulb,
  Megaphone,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { resolveUserAvatar } from '../../utils/userAvatar';

/**
 * ============================================================================
 *  AdminSidebar — the ONE shared navigation for every admin page.
 * ----------------------------------------------------------------------------
 *  Previously each admin page (MainAdmin + Coupons + Razorpay + Settings…)
 *  rendered its own hand-rolled, flat, and slightly-different sidebar, so the
 *  nav visibly "changed" when you navigated between them. This component is the
 *  single source of truth: identical grouped + collapsible nav everywhere.
 *
 *  - Grouped into sections (Overview / Customers / Revenue / Growth / System).
 *  - Collapsible; by default ONLY the first section is expanded.
 *  - Micro-animated (smooth height/opacity on expand, chevron rotate, hover).
 *
 *  Navigation model
 *  ----------------
 *  Items are either `tab` (an in-page view of MainAdmin, keyed by activeTab)
 *  or `route` (a sibling admin page).
 *   • On MainAdmin, pass `onSelectTab` so tab items switch the view in place.
 *   • On sibling pages, omit it — tab items navigate to `/main-admin?tab=<key>`
 *     and MainAdmin reads that param on load.
 *
 *  `activeKey` marks the current item so highlighting is correct on every page
 *  (e.g. "coupons" on the Coupon page, "organizations" on MainAdmin's default).
 * ============================================================================
 */

export const buildNavGroups = () => [
  {
    section: 'Overview',
    items: [
      { key: 'organizations', label: 'Organizations', icon: Building, type: 'tab' },
      { key: 'posthog', label: 'Analytics', icon: BarChart3, type: 'tab' },
      { key: 'activity', label: 'Activity', icon: Activity, type: 'tab' },
      // ADDITIVE: per-org user behaviour pattern — logins, rhythm, page footprint, friction
      { key: 'org-pattern', label: 'Org Patterns', icon: Fingerprint, type: 'route', path: '/main-admin/org-pattern' },
    ],
  },
  {
    section: 'Customers',
    items: [
      { key: 'leads', label: 'Leads', icon: UserCheck, type: 'tab' },
      { key: 'suggestions', label: 'Suggestions', icon: Lightbulb, type: 'route', path: '/main-admin/suggestions' },
      { key: 'announcements', label: 'Announcements', icon: Megaphone, type: 'route', path: '/main-admin/announcements' },
      { key: 'partners', label: 'B2B Partners', icon: UserCheck, type: 'tab' },
      { key: 'newsletter', label: 'Newsletter', icon: Mail, type: 'tab' },
    ],
  },
  {
    section: 'Revenue',
    items: [
      { key: 'revenue', label: 'Revenue', icon: TrendingUp, type: 'route', path: '/main-admin/revenue' },
      { key: 'coupons', label: 'Coupon Management', icon: Tag, type: 'route', path: '/main-admin/coupons' },
      { key: 'razorpay-coupons', label: 'Razorpay Coupons', icon: IndianRupee, type: 'route', path: '/main-admin/razorpay-coupons' },
      { key: 'razorpay-links', label: 'Razorpay Links', icon: LinkIcon, type: 'route', path: '/main-admin/razorpay-links' },
    ],
  },
  {
    section: 'Growth',
    items: [
      { key: 'blogs', label: 'Blog Management', icon: FileText, type: 'tab' },
      { key: 'email-campaigns', label: 'Email Campaigns', icon: Mail, type: 'tab' },
      { key: 'report-generator', label: 'Report Generator', icon: FileText, type: 'tab' },
      { key: 'weekly-reports', label: 'Weekly Reports', icon: CalendarClock, type: 'route', path: '/main-admin/weekly-reports' },
    ],
  },
  {
    section: 'System',
    items: [
      { key: 'scheduler', label: 'Scheduler Logs', icon: Clock, type: 'tab' },
      { key: 'ai-prompts', label: 'AI Prompts', icon: Brain, type: 'tab' },
      { key: 'system-logics', label: 'Critical Logics', icon: Code, type: 'tab' },
      { key: 'error-logs', label: 'Error Logs', icon: AlertCircle, type: 'route', path: '/main-admin/error-logs' },
      { key: 'settings', label: 'Settings', icon: Settings, type: 'route', path: '/main-admin/settings' },
    ],
  },
];

const AdminSidebar = ({ activeKey = 'organizations', onSelectTab }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const groups = useMemo(() => buildNavGroups(), []);

  // Sections open by default: Overview + Customers, plus whichever section
  // owns the currently-active item (so the active link is always visible).
  const DEFAULT_OPEN = ['Overview', 'Customers'];
  const initialCollapsed = useMemo(() => {
    const state = {};
    groups.forEach((g) => {
      const ownsActive = g.items.some((it) => it.key === activeKey);
      state[g.section] = !(DEFAULT_OPEN.includes(g.section) || ownsActive);
    });
    return state;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const toggle = useCallback((section) => {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleClick = useCallback(
    (item) => {
      if (item.type === 'route') {
        navigate(item.path);
      } else if (onSelectTab) {
        onSelectTab(item.key);
      } else {
        // Sibling page → jump back to MainAdmin on the requested tab.
        navigate(`/main-admin?tab=${item.key}`);
      }
    },
    [navigate, onSelectTab]
  );

  const userLabel = user?.name || user?.email || 'Admin';
  const userInitials = userLabel
    .replace(/[^A-Za-z ]/g, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || 'AD';
  // Pretty-print the raw role (e.g. "main_admin" → "Super Admin").
  const roleLabel = user?.role === 'main_admin'
    ? 'Super Admin'
    : (user?.role || 'Super Admin')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="hidden md:flex md:flex-shrink-0">
      <div className="flex flex-col w-64">
        <div className="tg-sidebar flex flex-col h-0 flex-1">
          <div className="flex-1 flex flex-col pt-3 pb-3 overflow-y-auto">
            {/* Brand */}
            <div className="tg-sidebar-brand flex items-center flex-shrink-0 px-4 pb-3 mb-1">
              <div className="flex items-center gap-2.5">
                <div className="tg-sidebar-mark h-8 w-8 flex items-center justify-center rounded-lg overflow-hidden">
                  <img src="/logo-icon.svg" alt="TeamGrid" className="h-5 w-5 object-contain" />
                </div>
                <div>
                  <div className="tg-sidebar-brand-name text-[14px] font-bold leading-tight">Teamgrid</div>
                  <div className="tg-sidebar-brand-sub text-[11px] leading-tight mt-[1px]">Super Admin</div>
                </div>
              </div>
            </div>

            {/* Grouped, collapsible nav */}
            <nav className="flex-1 px-2.5 space-y-0.5">
              {groups.map((group) => {
                const isCollapsed = collapsed[group.section];
                return (
                  <div key={group.section} className="tg-nav-group">
                    <button
                      type="button"
                      onClick={() => toggle(group.section)}
                      className="tg-nav-section-header flex items-center justify-between w-full font-semibold uppercase tracking-wider px-2.5 pt-3 pb-1 rounded-md"
                    >
                      <span>{group.section}</span>
                      <ChevronDown
                        className={`h-3 w-3 transition-transform duration-300 ${isCollapsed ? '-rotate-90' : ''}`}
                      />
                    </button>
                    <div className={`tg-nav-items ${isCollapsed ? 'is-collapsed' : ''}`}>
                      <div className="tg-nav-items-inner flex flex-col gap-px">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const active = item.key === activeKey;
                          return (
                            <button
                              key={item.key}
                              onClick={() => handleClick(item)}
                              className={`tg-nav-item flex items-center gap-2.5 px-2.5 py-[7px] text-[13px] font-medium w-full text-left ${active ? 'is-active' : ''}`}
                            >
                              <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
                              <span className="truncate">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>

          {/* User / logout */}
          <div className="tg-sidebar-foot flex-shrink-0 flex items-center gap-2.5 p-3">
            {resolveUserAvatar(user) ? (
              <img src={resolveUserAvatar(user)} alt="" className="tg-sidebar-avatar h-8 w-8 rounded-full flex-shrink-0 object-cover" />
            ) : (
            <div className="tg-sidebar-avatar h-8 w-8 rounded-full flex items-center justify-center text-[11px] flex-shrink-0">
              {userInitials}
            </div>
            )}
            <div className="tg-sidebar-user flex-1 min-w-0">
              <b className="block text-[12.5px] font-semibold truncate">{userLabel}</b>
              <span className="block text-[11px] truncate">{roleLabel}</span>
            </div>
            <button
              onClick={logout}
              className="tg-sidebar-logout p-2 rounded-lg focus:outline-none"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
